import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Columns3, Ellipsis } from 'lucide-react';
import Menu from './Menu';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import { SkeletonRows } from './Loading';

const ROW_SIZE_OPTIONS = [50, 100, 200];
const formatNumber = (n) => Number(n || 0).toLocaleString('en-US');

const pageWindow = (page, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  let start = Math.max(1, page - 2);
  let end = Math.min(totalPages - 2, page + 2);
  if (page <= 3) {
    start = 1;
    end = 5;
  } else if (page >= totalPages - 4) {
    start = totalPages - 6;
    end = totalPages - 2;
  }
  const pages = [0];
  if (start > 1) pages.push('gap-start');
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < totalPages - 2) pages.push('gap-end');
  pages.push(totalPages - 1);
  return pages;
};

// Hidden column keys, persisted per table in localStorage under storageKey.
export function useColumnVisibility(columns, storageKey) {
  const defaults = useMemo(() => columns.filter((c) => c.defaultHidden).map((c) => c.key), [columns]);
  const [hidden, setHidden] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey));
      if (Array.isArray(stored)) return stored;
    } catch (_err) {
      // fall through to defaults
    }
    return defaults;
  });

  const update = useCallback(
    (next) => {
      localStorage.setItem(storageKey, JSON.stringify(next));
      setHidden(next);
    },
    [storageKey],
  );

  const toggle = useCallback((key) => update(hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key]), [hidden, update]);
  const reset = useCallback(() => update(defaults), [defaults, update]);

  return { hidden, toggle, reset };
}

export function ColumnsMenu({ columns, hidden, onToggle, onReset }) {
  const items = [
    ...columns
      .filter((c) => c.hideable !== false)
      .map((c) => ({ key: c.key, label: c.header, type: 'checkbox', checked: !hidden.includes(c.key), keepOpen: true, onSelect: () => onToggle(c.key) })),
    { key: 'divider', divider: true },
    { key: 'reset', label: 'Reset columns', onSelect: onReset },
  ];
  return (
    <Menu items={items} label="Show or hide columns" align="end">
      <Columns3 size={15} strokeWidth={1.75} className="text-graphite" aria-hidden="true" />
      Columns
    </Menu>
  );
}

const cellClass = (column) =>
  [
    'h-9 px-3 border-b border-rule whitespace-nowrap align-middle',
    column.align === 'right' ? 'text-right tabular-nums' : 'text-left',
    column.mono ? 'font-mono text-id' : '',
  ].join(' ');

function Cell({ column, row }) {
  const content = column.render ? column.render(row) : row[column.key];
  if (!column.maxWidth && !column.grow) return <td className={cellClass(column)}>{content}</td>;
  return (
    <td className={`${cellClass(column)} ${column.grow ? 'max-w-0' : ''}`}>
      <div className="truncate" style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined} title={column.fullText ? column.fullText(row) : undefined}>
        {content}
      </div>
    </td>
  );
}

const headerStyle = (column) => {
  if (column.grow) return { width: '100%', minWidth: column.minWidth || 200 };
  return column.width ? { width: column.width } : undefined;
};

function rangeLabel({ total, page, rowsPerPage, loading, error }) {
  if (error) return '';
  if (total === 0) return loading ? 'Loading…' : 'No results';
  const first = page * rowsPerPage + 1;
  const last = Math.min((page + 1) * rowsPerPage, total);
  return `${formatNumber(first)}–${formatNumber(last)} of ${formatNumber(total)}`;
}

function Pagination({ total, page, rowsPerPage, onPageChange, onRowsPerPageChange, loading, error }) {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const pageButton = 'inline-flex items-center justify-center min-w-7 h-7 px-1.5 rounded-control border tabular-nums';

  return (
    <div className="shrink-0 flex items-center gap-5 h-11 px-3 border-t border-rule text-small text-graphite">
      <span className="min-w-[120px] tabular-nums">{rangeLabel({ total, page, rowsPerPage, loading, error })}</span>
      {onRowsPerPageChange && (
        <label className="flex items-center gap-1.5">
          Rows
          <select
            value={rowsPerPage}
            onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
            className="h-7 pl-1.5 pr-6 rounded-control border border-rule bg-sheet text-small text-ink"
          >
            {ROW_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      )}
      <nav aria-label="Pagination" className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          aria-label="Previous page"
          className={`${pageButton} border-rule text-ink hover:bg-hover disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <ChevronLeft size={15} aria-hidden="true" />
        </button>
        {pageWindow(page, totalPages).map((p) =>
          typeof p === 'string' ? (
            <span key={p} className="px-1">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={`${pageButton} ${p === page ? 'border-signal bg-signal text-signal-ink font-semibold' : 'border-rule text-ink hover:bg-hover'}`}
            >
              {p + 1}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          aria-label="Next page"
          className={`${pageButton} border-rule text-ink hover:bg-hover disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <ChevronRight size={15} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}

function Checkbox({ checked, indeterminate = false, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} aria-label={label} className="h-4 w-4 align-middle accent-signal cursor-pointer" />;
}

/**
 * Server-paginated table.
 * columns: [{ key, header, render?(row), align?: 'right', mono?, maxWidth?, grow?, minWidth?, fullText?(row), width?, hideable?, defaultHidden? }]
 * maxWidth truncates at a fixed width; grow makes the column take the remaining width and truncate.
 * rowMenu(row) returns Menu items for the row's ⋯ button; rowLabel(row) names the row for screen readers.
 * pagination: { total, page, rowsPerPage, onPageChange, onRowsPerPageChange? }
 * selection: { selected: [rowKey…], onChange(nextKeys) } adds a checkbox column (needs rowKey).
 * selectionBar: shown over the header row while rows are selected, for bulk actions.
 */
export default function DataTable({
  label,
  columns,
  rows,
  rowKey,
  rowLabel,
  hidden = [],
  loading = false,
  error = null,
  errorTitle = 'This table could not be loaded',
  onRetry,
  empty,
  rowMenu,
  pagination,
  selection,
  selectionBar,
  className = '',
}) {
  const scrollRef = useRef(null);
  const shown = columns.filter((c) => !hidden.includes(c.key));
  const colSpan = shown.length + (rowMenu ? 1 : 0) + (selection ? 1 : 0);
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const page = pagination?.page;

  const selected = selection?.selected || [];
  const pageKeys = selection && hasRows ? rows.map(rowKey) : [];
  const selectedOnPage = pageKeys.filter((k) => selected.includes(k)).length;
  const allOnPage = pageKeys.length > 0 && selectedOnPage === pageKeys.length;
  const toggleRow = (k) => selection.onChange(selected.includes(k) ? selected.filter((s) => s !== k) : [...selected, k]);
  const togglePage = () => selection.onChange(allOnPage ? selected.filter((k) => !pageKeys.includes(k)) : [...new Set([...selected, ...pageKeys])]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  let body;
  if (error) {
    body = (
      <tr>
        <td colSpan={colSpan}>
          <ErrorState title={errorTitle} error={error} onRetry={onRetry} />
        </td>
      </tr>
    );
  } else if (loading && !hasRows) {
    body = <SkeletonRows columns={colSpan} />;
  } else if (!hasRows) {
    body = (
      <tr>
        <td colSpan={colSpan}>{empty || <EmptyState title="Nothing to show" />}</td>
      </tr>
    );
  } else {
    body = rows.map((row, index) => {
      const k = rowKey ? rowKey(row) : index;
      const isSelected = Boolean(selection) && selected.includes(k);
      const name = rowLabel ? rowLabel(row) : `row ${index + 1}`;
      return (
        <tr key={k} className={`group ${isSelected ? 'bg-signal-soft/60' : 'hover:bg-hover'}`} aria-selected={selection ? isSelected : undefined}>
          {selection && (
            <td className="w-10 pl-3 pr-1 border-b border-rule">
              <Checkbox checked={isSelected} onChange={() => toggleRow(k)} label={`Select ${name}`} />
            </td>
          )}
          {shown.map((column) => (
            <Cell key={column.key} column={column} row={row} />
          ))}
          {rowMenu && (
            <td className="w-10 px-1 border-b border-rule text-center">
              <Menu items={rowMenu(row)} label={`Actions for ${name}`} align="end" variant="ghost" size="sm" square>
                <Ellipsis size={16} className="text-graphite" aria-hidden="true" />
              </Menu>
            </td>
          )}
        </tr>
      );
    });
  }

  const busy = loading && hasRows;

  return (
    <div className={`relative flex flex-col min-h-0 bg-sheet border border-rule rounded-panel overflow-hidden ${className}`}>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        <table aria-label={label} aria-busy={loading} className="w-full border-collapse text-body text-ink">
          <thead>
            <tr>
              {selection && (
                <th scope="col" className="sticky top-0 z-[1] w-10 pl-3 pr-1 bg-sheet shadow-[inset_0_-1px_0_rgb(var(--rule))] text-left">
                  <Checkbox
                    checked={allOnPage}
                    indeterminate={selectedOnPage > 0 && !allOnPage}
                    onChange={togglePage}
                    label={allOnPage ? 'Deselect all rows on this page' : 'Select all rows on this page'}
                  />
                </th>
              )}
              {shown.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={headerStyle(column)}
                  className={`sticky top-0 z-[1] h-9 px-3 bg-sheet shadow-[inset_0_-1px_0_rgb(var(--rule))] text-small font-semibold text-graphite whitespace-nowrap ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {column.header}
                </th>
              ))}
              {rowMenu && (
                <th scope="col" className="sticky top-0 z-[1] w-10 bg-sheet shadow-[inset_0_-1px_0_rgb(var(--rule))]">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className={busy ? 'opacity-60 transition-opacity' : undefined}>{body}</tbody>
        </table>
      </div>
      {selection && selected.length > 0 && selectionBar && (
        <div className="absolute top-0 left-10 right-0 z-[2] h-9 flex items-center gap-2 pl-3 pr-2 bg-signal-soft shadow-[inset_0_-1px_0_rgb(var(--rule))] text-body text-ink">
          {selectionBar}
        </div>
      )}
      {busy && (
        <div className="absolute inset-x-0 top-9 h-0.5 overflow-hidden" aria-hidden="true">
          <div className="progress-slide h-full w-1/3 bg-signal" />
        </div>
      )}
      {pagination && <Pagination {...pagination} loading={loading} error={error} />}
    </div>
  );
}
